import Vue from 'vue'
import VueRouter from 'vue-router'

import AdminLayout from '@/views/layouts/AdminLayout.vue'
import AppLayout from '@/views/layouts/AppLayout.vue'
import jwtDecode from 'jwt-decode'

Vue.use(VueRouter)

export const router = new VueRouter({
  routes: [
    {
      path: '/login',
      name: 'Login',
      meta: {
        requiresAuth: false
      },
      component: () => import('@/views/auth/LoginPage.vue')
    },
    {
      path: '/reset/:passwordResetToken',
      name: 'ResetPassword',
      meta: {
        requiresAuth: false
      },
      component: () => import('@/views/auth/ResetPasswordPage.vue')
    },
    {
      path: '/register',
      name: 'Register',
      meta: {
        requiresAuth: false
      },
      component: () => import('@/views/auth/RegisterPage.vue')
    },
    {
      path: '/forgotPassword',
      name: 'ForgotPassword',
      meta: {
        requiresAuth: false
      },
      component: () => import('@/views/auth/ForgotPasswordPage.vue')
    },
    {
      path: '/',
      redirect: { name: 'App' }
    },
    {
      path: '/app',
      name: 'App',
      meta: {
        requiresAuth: true,
        roles: [ 'Administrator', 'User' ]
      },
      component: AppLayout,
      redirect: { name: 'Quotes' },
      children: [
        {
          path: 'quotes',
          name: 'Quotes',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/quote/QuotesPage.vue')
        },
        {
          path: 'quote/:id',
          name: 'Quote',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/quote/QuotePage.vue')
        },
        {
          path: 'configure',
          name: 'Configure',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/configure/ConfigurePage.vue')
        },
        {
          path: 'profile',
          name: 'Profile',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/ProfilePage.vue')
        },
        {
          path: 'error',
          name: 'Error',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/Error.vue')
        },
        {
          path: 'kitInformationPage/:kitId/:orgId',
          name: 'KitInformationPage',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/KitInformationPage.vue')
        },
        {
          path: 'kitMaterialInfoPage/:kitMaterialId',
          name: 'KitMaterialInfoPage',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'User' ]
          },
          component: () => import('@/views/app/KitMaterialInfoPage.vue')
        },
        {
          path: 'sales-dashboard',
          name: 'SalesDashboard',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator', 'Sales' ]
          },
          component: () => import('@/views/sales/SalesDashboard.vue')
        }
      ]
    },
    {
      path: '/admin',
      name: 'Admin',
      meta: {
        requiresAuth: true,
        roles: [ 'Administrator' ]
      },
      component: AdminLayout,
      redirect: { name: 'AdminDashboard' },
      children: [
        {
          path: '',
          name: 'AdminDashboard',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/dashboard/AdminDashboard.vue')
        },
        {
          path: 'actuators',
          name: 'Actuators',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/actuators/ActuatorsPage.vue')
        },
        {
          path: 'actuators/import',
          name: 'ActuatorsImport',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/actuators/components/ActuatorImport.vue')
        },
        {
          path: 'valves/import',
          name: 'ValveImport',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/valves/components/ValveImport.vue')
        },
        {
          path: 'kits/import',
          name: 'KitImport',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/kits/components/KitImport.vue')
        },
        {
          path: 'valves',
          name: 'Valves',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/valves/ValvesPage.vue')
        },
        {
          path: 'importvalves',
          name: 'ImportValves',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/valves/ImportValvesPage.vue')
        },
        {
          path: 'kits',
          name: 'Kits',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/kits/KitsPage.vue')
        },
        {
          path: 'materials',
          name: 'Materials',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/kits/MaterialsPage.vue')
        },
        {
          path: 'options',
          name: 'Options',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/kits/OptionsPage.vue')
        },
        {
          path: 'organizations',
          name: 'Organizations',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/organizations/OrganizationsPage.vue')
        },
        {
          path: 'organization/:id',
          name: 'Organization',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/organizations/OrganizationPage.vue')
        },
        {
          path: 'users',
          name: 'Users',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/users/UsersPage.vue')
        },
        {
          path: 'messages',
          name: 'Messages',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/messages/MessagesPage.vue')
        },
        {
          path: 'documents',
          name: 'Documents',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/documents/DocumentsPage.vue')
        },
        {
          path: 'salespeople',
          name: 'SalesPeople',
          meta: {
            requiresAuth: true,
            roles: [ 'Administrator' ]
          },
          component: () => import('@/views/admin/salespeople/SalespeoplePage.vue')
        }
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  // redirect to login page if not logged in and trying to access a restricted page
  var currentUser = JSON.parse(localStorage.getItem('currentUser'))
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    // also check for expiry token
    // redirect to login page if not logged in and trying to access a restricted page
    if (currentUser && to.matched.some(record => record.meta.roles.some(role => currentUser.roles.includes(role))) && Date.now() < jwtDecode(currentUser.token).exp * 1000) {
      next()
    } else {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
  } else {
    next() // make sure to always call next()!
  }
})



// WEBPACK FOOTER //
// ./src/router/router.js